import { Text } from '@/components/ui/text';
import { LinearGradient } from 'expo-linear-gradient';
import React, { useId, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import Svg, { Circle, Defs, Line, Path, Pattern, Rect } from 'react-native-svg';

// Deterministic hash from string to number
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0;
  }
  return Math.abs(hash);
}

// Generate a rich, saturated color palette from a name
function generateColors(name: string) {
  const hash = hashString(name);
  const hue = hash % 360;
  const saturation = 55 + (hash % 25);
  const lightness = 35 + (hash % 15);

  const primary = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  const secondary = `hsl(${(hue + 30) % 360}, ${saturation - 10}%, ${lightness + 15}%)`;
  const accent = `hsl(${(hue + 60) % 360}, ${saturation - 5}%, ${lightness + 25}%)`;

  return { primary, secondary, accent, hue, hash };
}

// Calculate font size based on name length and container dimensions
function calculateFontSize(name: string, width: number, height: number): number {
  // When aspect ratio is wider than 2:1, use height as basis to prevent oversized text
  const basis = width / height > 2 ? height : width;
  const maxSize = basis * 0.12;
  const minSize = basis * 0.045;

  const charCount = name.length;
  let size: number;

  if (charCount <= 6) {
    size = maxSize;
  } else if (charCount <= 12) {
    size = maxSize * 0.85;
  } else if (charCount <= 20) {
    size = maxSize * 0.65;
  } else if (charCount <= 30) {
    size = maxSize * 0.5;
  } else {
    size = maxSize * 0.4;
  }

  return Math.max(size, minSize);
}

// Check if name should wrap to two lines
function shouldWrap(name: string): boolean {
  return name.length > 14;
}

// Split name into balanced lines
function splitName(name: string): string[] {
  if (!shouldWrap(name)) return [name];

  const words = name.split(/\s+/);
  if (words.length === 1) return [name];

  const mid = name.length / 2;
  let bestSplit = 0;
  let bestDiff = Infinity;
  let charCount = 0;

  for (let i = 0; i < words.length - 1; i++) {
    charCount += words[i].length + 1;
    const diff = Math.abs(charCount - mid);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestSplit = i + 1;
    }
  }

  const line1 = words.slice(0, bestSplit).join(' ');
  const line2 = words.slice(bestSplit).join(' ');
  return [line1, line2];
}

// Small utensil icon using react-native-svg
function UtensilIcon({ color, size = 18 }: { color: string; size?: number }) {
  return (
    <Svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <Path d="M3 2v7c0 1.1.9 2 2 2h2a2 2 0 002-2V2" />
      <Path d="M6 2v20" />
      <Path d="M18 2c0 0 2 3 2 7s-2 5-2 5v8" />
    </Svg>
  );
}

// Decorative geometric pattern based on hash
function PatternOverlay({ hash, width, height, instanceId }: { hash: number; width: number; height: number; instanceId: string }) {
  const patternType = hash % 4;
  const opacity = 0.08;

  if (patternType === 0) {
    return (
      <View style={StyleSheet.absoluteFill} pointerEvents="none">
        <Svg width={width} height={height}>
          <Defs>
            <Pattern
              id={`diag-${instanceId}`}
              width={20}
              height={20}
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(45)"
            >
              <Line x1={0} y1={0} x2={0} y2={20} stroke="white" strokeWidth={2} opacity={opacity} />
            </Pattern>
          </Defs>
          <Rect width={width} height={height} fill={`url(#diag-${instanceId})`} />
        </Svg>
      </View>
    );
  }

  if (patternType === 1) {
    return (
      <View style={StyleSheet.absoluteFill} pointerEvents="none">
        <Svg width={width} height={height}>
          <Defs>
            <Pattern id={`dots-${instanceId}`} width={24} height={24} patternUnits="userSpaceOnUse">
              <Circle cx={12} cy={12} r={2} fill="white" opacity={opacity} />
            </Pattern>
          </Defs>
          <Rect width={width} height={height} fill={`url(#dots-${instanceId})`} />
        </Svg>
      </View>
    );
  }

  if (patternType === 2) {
    return (
      <View style={StyleSheet.absoluteFill} pointerEvents="none">
        <Svg width={width} height={height}>
          <Defs>
            <Pattern id={`grid-${instanceId}`} width={30} height={30} patternUnits="userSpaceOnUse">
              <Path d="M 30 0 L 0 0 0 30" fill="none" stroke="white" strokeWidth={1} opacity={opacity} />
            </Pattern>
          </Defs>
          <Rect width={width} height={height} fill={`url(#grid-${instanceId})`} />
        </Svg>
      </View>
    );
  }

  // Pattern 3: Soft circles (absolute positions, no pattern/defs needed)
  const cx1 = width * 0.85;
  const cy1 = height * 0.75;
  const r1 = Math.min(width, height) * 0.4;
  const cx2 = width * 0.1;
  const cy2 = height * 0.2;
  const r2 = Math.min(width, height) * 0.25;

  return (
    <View style={StyleSheet.absoluteFill} pointerEvents="none">
      <Svg width={width} height={height}>
        <Circle cx={cx1} cy={cy1} r={r1} fill="white" opacity={0.04} />
        <Circle cx={cx2} cy={cy2} r={r2} fill="white" opacity={0.03} />
      </Svg>
    </View>
  );
}

interface RestaurantPlaceholderProps {
  name?: string | null;
  width: number;
  height?: number;
}

const RestaurantPlaceholder = React.memo<RestaurantPlaceholderProps>(({ name, width, height: heightProp }) => {
  const instanceId = useId();
  const resolvedName = name?.trim() || '';
  const height = heightProp ?? Math.round(width / 2);

  const { colors, fontSize, lines } = useMemo(() => {
    const c = generateColors(resolvedName);
    const fs = calculateFontSize(resolvedName, width, height);
    const l = splitName(resolvedName);
    return { colors: c, fontSize: fs, lines: l };
  }, [resolvedName, width, height]);

  if (width <= 0) return null;

  const letterSpacing = fontSize > width * 0.08 ? 2 : 0.5;
  const lineHeight = fontSize * 1.2;

  return (
    <LinearGradient
      colors={[colors.primary, colors.secondary, colors.accent]}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={{ width, height, overflow: 'hidden' }}
    >
      <PatternOverlay hash={colors.hash} width={width} height={height} instanceId={instanceId} />

      {/* Main content */}
      <View style={styles.content}>
        {/* Name lines */}
        <View style={styles.textContainer}>
          {lines.map((line, i) => (
            <Text
              key={i}
              style={[
                styles.nameText,
                {
                  fontSize,
                  letterSpacing,
                  lineHeight,
                  textShadowColor: 'rgba(0,0,0,0.2)',
                  textShadowOffset: { width: 0, height: 2 },
                  textShadowRadius: 16,
                },
              ]}
            >
              {line}
            </Text>
          ))}
        </View>

        {/* Utensil divider */}
        <View style={styles.divider}>
          <View style={styles.dividerLine} />
          <UtensilIcon color="rgba(255,255,255,0.7)" size={14} />
          <View style={styles.dividerLine} />
        </View>
      </View>
    </LinearGradient>
  );
});

const styles = StyleSheet.create({
  content: {
    ...StyleSheet.absoluteFill,
    zIndex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingHorizontal: 24,
  },
  textContainer: {
    alignItems: 'center',
  },
  nameText: {
    fontWeight: '700',
    color: 'rgba(255, 255, 255, 0.88)',
  },
  divider: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    opacity: 0.45,
    marginTop: 2,
  },
  dividerLine: {
    width: 20,
    height: 1,
    backgroundColor: 'rgba(255,255,255,0.5)',
  },
});

RestaurantPlaceholder.displayName = 'RestaurantPlaceholder';

export default RestaurantPlaceholder;
export { calculateFontSize, generateColors, hashString, shouldWrap, splitName };

